import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, F, IMG } from '../theme';

const primaryBtn = (extra = {}) => ({
  background: C.orange, color: '#fff', border: 'none', fontFamily: 'inherit',
  fontWeight: 800, borderRadius: 999, cursor: 'pointer', ...extra,
});
const eyebrow = { textTransform: 'uppercase', letterSpacing: '.14em', fontSize: 13, fontWeight: 800, color: C.orange, margin: '0 0 14px' };
const h2 = (extra = {}) => ({ fontFamily: F.serif, fontWeight: 500, lineHeight: 1.1, color: C.tealDeep, margin: 0, ...extra });

const TRUST = [
  { icon: '★', title: 'Trained & experienced', body: 'Registered nurses, geriatric specialists, and professionals with real caregiving experience.' },
  { icon: '✓', title: 'Fully vetted', body: 'Identity, licence, experience, criminal record, guarantors and references — all verified.' },
  { icon: '♡', title: 'Family-rated', body: 'Every caregiver is reviewed after each engagement, so quality stays high.' },
];
const WHO = [
  { img: `${IMG}/care-elderly-man.png`, title: 'Elderly parents', body: 'Dependable, dignified daily support — bathing, dressing, meals, mobility, medication reminders and companionship — for parents who deserve to age with comfort and respect.' },
  { img: `${IMG}/care-child.png`, title: 'Children with special needs', body: 'Patient, qualified, consistent care built around each child, working closely with the family toward a shared routine and clear goals.' },
];
const STEPS = [
  { n: '1', title: 'Tell us what you need', body: 'Through our form, you describe who needs care, the kind of help required, and your location and preferences. It takes only a few minutes.' },
  { n: '2', title: 'We match you with a verified caregiver', body: "We propose a fully vetted, nurse-trained caregiver who fits your loved one's needs. You decide who feels right — there's no obligation until you do." },
  { n: '3', title: 'Care begins, with a clear plan', body: 'Your caregiver works to a personalised care plan and the daily essentials are handled. After each engagement you can rate and review, so standards keep rising.' },
];
const WHY = [
  { icon: '✓', title: 'Verification is the heart, not a checkbox', body: "Most options simply hand you a stranger and hope for the best. We treat thorough vetting as the core of what we do — it's the hardest thing to copy, and exactly what earns your trust." },
  { icon: '♡', title: 'Care managed, not just matched', body: "We don't disappear after the introduction. CarelyNest stands behind every placement with personalised care plans, ongoing oversight, and a feedback loop that keeps quality high." },
];

export default function Home() {
  const navigate = useNavigate();
  const howRef = useRef(null);

  const scrollHow = (e) => {
    e.preventDefault();
    if (howRef.current) window.scrollTo({ top: howRef.current.offsetTop - 70, behavior: 'smooth' });
  };

  return (
    <div className="cn-fade">
      {/* HERO */}
      <section style={{ position: 'relative', background: 'linear-gradient(180deg,#F4EFE4 0%,#FBF7F0 100%)', overflow: 'hidden' }}>
        <div data-cn-hero-grid style={{ maxWidth: 1200, margin: '0 auto', padding: '72px 24px 40px', display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 56, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.sageTint, color: C.teal, fontWeight: 800, fontSize: 13, letterSpacing: '.04em', textTransform: 'uppercase', padding: '8px 14px', borderRadius: 999, marginBottom: 22 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.orange }} />Trusted home care
            </div>
            <h1 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 60, lineHeight: 1.04, letterSpacing: '-.02em', color: C.tealDeep, margin: '0 0 20px' }}>Where compassion feels like home</h1>
            <p style={{ fontSize: 19, lineHeight: 1.65, color: C.body, maxWidth: 520, margin: '0 0 32px' }}>CarelyNest connects families with thoroughly vetted, nurse-trained caregivers and geriatric specialists for elderly parents and children with special needs. We do the hard part — verifying, matching, and managing — so you can be at peace.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
              <button className="cn-primary-btn" onClick={() => navigate('/contact')} style={primaryBtn({ fontSize: 17, padding: '16px 30px', boxShadow: '0 14px 30px -12px rgba(224,124,69,.75)' })}>Find a caregiver</button>
              <a href="#how-it-works" onClick={scrollHow} className="cn-secondary-link" style={{ fontSize: 17, fontWeight: 800, color: C.teal, padding: '16px 20px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>How it works<span style={{ fontSize: 20 }}>→</span></a>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 'auto -18px -18px auto', width: '70%', height: '70%', background: C.sageTint, borderRadius: 28, zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: '-16px auto auto -16px', width: '56%', height: '56%', background: 'rgba(224,124,69,.16)', borderRadius: 28, zIndex: 0 }} />
            <img src={`${IMG}/care-elderly-woman.png`} alt="A CarelyNest caregiver with an elderly client" style={{ position: 'relative', zIndex: 1, width: '100%', height: 520, objectFit: 'cover', borderRadius: 24, boxShadow: '0 40px 60px -30px rgba(21,58,51,.4)' }} />
          </div>
        </div>

        {/* TRUST STRIP */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 24px 64px' }}>
          <div data-cn-trust-grid style={{ background: '#fff', border: '1px solid #EEE7D9', borderRadius: 20, boxShadow: '0 24px 50px -34px rgba(21,58,51,.3)', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)' }}>
            {TRUST.map((t) => (
              <div key={t.title} data-cn-trust-cell style={{ padding: '28px 30px', borderRight: '1px solid #F0EADD' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ width: 38, height: 38, borderRadius: 11, background: C.sageTint, color: C.teal, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 18 }}>{t.icon}</span>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.ink }}>{t.title}</h3>
                </div>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: C.muted }}>{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <p style={eyebrow}>The CarelyNest promise</p>
        <p style={{ fontFamily: F.serif, fontWeight: 400, fontSize: 33, lineHeight: 1.4, color: '#1B3A33', margin: 0, textWrap: 'pretty' }}>Two things have to be true for a family to feel at peace: you must trust the person you let into your home, and you must know your loved one is in capable hands. We make trust the default — every caregiver is verified, trained, and accountable — so you can hand over the care without handing over your peace of mind.</p>
      </section>

      {/* WHO WE CARE FOR */}
      <section style={{ background: C.sage }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '90px 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 52px' }}>
            <h2 style={h2({ fontSize: 44, marginBottom: 14 })}>Who we care for</h2>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: C.body, margin: 0 }}>Personalised, dignified care built around the person — never a one-size-fits-all package.</p>
          </div>
          <div data-cn-who-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {WHO.map((w) => (
              <div key={w.title} style={{ background: '#fff', borderRadius: 22, overflow: 'hidden', border: '1px solid #E6EDE7', boxShadow: '0 26px 50px -38px rgba(21,58,51,.5)' }}>
                <img src={w.img} alt={w.title} style={{ width: '100%', height: 280, objectFit: 'cover' }} />
                <div style={{ padding: '30px 32px 34px' }}>
                  <h3 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 27, color: '#173B34', margin: '0 0 12px' }}>{w.title}</h3>
                  <p style={{ fontSize: 16, lineHeight: 1.65, color: '#54635D', margin: 0 }}>{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" ref={howRef} style={{ maxWidth: 1200, margin: '0 auto', padding: '96px 24px' }}>
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 60px' }}>
          <p style={eyebrow}>How it works</p>
          <h2 style={h2({ fontSize: 44 })}>Three simple steps to confident care</h2>
        </div>
        <div data-cn-steps-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 26 }}>
          {STEPS.map((s) => (
            <div key={s.n} style={{ background: '#fff', border: '1px solid #EDE6D8', borderRadius: 20, padding: '34px 30px', position: 'relative' }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: C.teal, color: '#fff', fontFamily: F.serif, fontSize: 26, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>{s.n}</div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: '0 0 10px' }}>{s.title}</h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.62, color: '#5A6963', margin: 0 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY FAMILIES CHOOSE */}
      <section style={{ background: C.tealDeep, color: C.sageTint }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '92px 24px' }}>
          <h2 style={h2({ fontSize: 44, color: '#fff', margin: '0 0 48px', maxWidth: 600 })}>Why families choose CarelyNest</h2>
          <div data-cn-why-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {WHY.map((w) => (
              <div key={w.title} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 20, padding: '36px 34px' }}>
                <div style={{ width: 46, height: 46, borderRadius: 13, background: C.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, fontSize: 22 }}>{w.icon}</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>{w.title}</h3>
                <p style={{ fontSize: 16, lineHeight: 1.65, color: '#C4D6CF', margin: 0 }}>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '96px 24px' }}>
        <div style={{ background: 'linear-gradient(120deg,#F6E9DB 0%,#EAF2EE 100%)', borderRadius: 28, padding: '70px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <h2 style={h2({ fontSize: 46, lineHeight: 1.08, margin: '0 0 16px' })}>Ready to find care you can trust?</h2>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: C.body, margin: '0 auto 32px', maxWidth: 520 }}>Tell us about your loved one and we'll match you with the right caregiver.</p>
          <button className="cn-primary-btn" onClick={() => navigate('/contact')} style={primaryBtn({ fontSize: 18, padding: '17px 38px', boxShadow: '0 16px 34px -14px rgba(224,124,69,.8)' })}>Get started</button>
        </div>
      </section>
    </div>
  );
}
